import React, { memo, useEffect } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { InternalFetchMe } from 'src/constants/actionTypes'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { useSignOut } from 'src/hooks/useSignOut'
import {
  getInternalAuthError,
  getInternalAuthLoading,
  getInternalIdentity,
} from 'src/selectors/internalAuth'
import { AuthScreen } from './AuthScreen'

/**
 * The gate every admin route sits behind.
 *
 * Signing in and having access are two different outcomes: WorkOS will authenticate any account
 * in the connection, and only `/v1/internal/auth/me` knows whether that person has an internal
 * role. So a refusal gets its own screen rather than letting someone into a shell where every
 * request answers 403.
 */
const RequireInternalAuthComponent = () => {
  const { isLoading, user } = useAuth()
  const dispatch = useDispatch()
  const location = useLocation()
  const identity = useSelector(getInternalIdentity)
  const identityLoading = useSelector(getInternalAuthLoading)
  const identityError = useSelector(getInternalAuthError)
  const handleSignOut = useSignOut()

  // Runs once per signed-in user. The identity outlives navigation, so re-entering a route does
  // not re-ask; `Logout` clearing the slice is what makes the next sign-in fetch again.
  useEffect(() => {
    if (!user || identity || identityLoading || identityError) {
      return
    }

    dispatch(InternalFetchMe.request())
  }, [dispatch, user, identity, identityLoading, identityError])

  if (isLoading) {
    return (
      <AuthScreen dataTestId="admin-auth-loading" title="GigSafe Admin" description="Loading…" />
    )
  }

  if (!user) {
    return (
      <Navigate
        to={ADMIN_ROUTES.LOGIN_URL}
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    )
  }

  if (identityError) {
    return (
      <AuthScreen dataTestId="admin-auth-no-access" title="No access" description={identityError}>
        <Typography variant="body2" css={tw`text-shades-500`}>
          Signed in as {user.email}
        </Typography>
        <Button
          size="medium"
          color="dark"
          className={tw`mt-2 w-full`}
          onClick={handleSignOut}
          data-testid="admin-auth-no-access-sign-out"
        >
          Sign out
        </Button>
      </AuthScreen>
    )
  }

  if (!identity) {
    return (
      <AuthScreen
        dataTestId="admin-auth-checking-access"
        title="GigSafe Admin"
        description="Checking your access…"
      />
    )
  }

  return <Outlet />
}

export const RequireInternalAuth = memo(RequireInternalAuthComponent)
